import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Users, CreditCard, Activity, DollarSign, Layers, Loader2, Save, Eye, Mail, UserCog, ScrollText, LogIn } from "lucide-react";
import AdminMonitor from "@/components/AdminMonitor";
import AdminActivityLog from "@/components/AdminActivityLog";
import AdminCompliance from "@/components/AdminCompliance";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="card-soft p-6">
    <Icon className="w-5 h-5 mb-4" style={{ color: accent || "#3A2DB8" }} />
    <div className="font-heading font-bold text-3xl">{value}</div>
    <div className="text-xs text-secondary mt-1">{label}</div>
  </div>
);

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [packages, setPackages] = useState([]);
  const [email, setEmail] = useState(null);
  const [envs, setEnvs] = useState([]);
  const [logins, setLogins] = useState([]);
  const { setUser } = useAuth();

  const openAs = async (c) => {
    const { data } = await api.post(`/auth/impersonate/${c.id}`);
    setUser(data);
    navigate("/dashboard");
  };
  const [saving, setSaving] = useState("");
  const navigate = useNavigate();

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/customers").then((r) => setCustomers(r.data));
    api.get("/admin/subscriptions").then((r) => setSubs(r.data));
    api.get("/admin/runs").then((r) => setRuns(r.data));
    api.get("/admin/packages").then((r) => setPackages(r.data));
    api.get("/admin/settings/email").then((r) => setEmail(r.data));
    api.get("/admin/environments").then((r) => setEnvs(r.data));
    api.get("/admin/logins").then((r) => setLogins(r.data));
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  const editPkg = (id, patch) => setPackages(packages.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const savePkg = async (pkg) => {
    setSaving(pkg.id);
    if (Number(pkg.monthly_price) < 0 || Number(pkg.onboarding_price) < 0 || Number.isNaN(Number(pkg.monthly_price)) || Number.isNaN(Number(pkg.onboarding_price))) {
      toast.error("Prices must be zero or positive numbers."); setSaving(""); return;
    }
    try {
      const body = {
        code: pkg.code, name: pkg.name, tagline: pkg.tagline,
        monthly_price: Number(pkg.monthly_price), onboarding_price: Number(pkg.onboarding_price),
        product: pkg.product,
        features: Array.isArray(pkg.features) ? pkg.features : String(pkg.features).split(",").map((s) => s.trim()),
        active: pkg.active, order: pkg.order,
      };
      const { data } = await api.put(`/admin/packages/${pkg.id}`, body);
      if (data.stripe_sync?.ok === false) toast.warning(`${pkg.name} saved, but Stripe sync failed: ${data.stripe_sync.error}`);
      else toast.success(`${pkg.name} pricing updated & synced to Stripe.`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(""); }
  };

  if (!stats) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#3A2DB8]" /></div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12" data-testid="admin-page">
      <p className="overline text-[#7C3AED] mb-2">Platform administration</p>
      <h1 className="font-heading font-bold text-4xl tracking-tighter mb-10">Admin console</h1>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <Stat icon={Users} label="Customers" value={stats.customers} />
        <Stat icon={CreditCard} label="Active subs" value={stats.active_subscriptions} accent="#0F9D77" />
        <Stat icon={DollarSign} label="MRR" value={`$${(stats.mrr || 0).toLocaleString()}`} accent="#FF8A3D" />
        <Stat icon={Activity} label="Agent runs" value={stats.total_runs} accent="#E5325F" />
        <Stat icon={Layers} label="Environments" value={stats.total_environments} accent="#7C3AED" />
      </div>

      <Tabs defaultValue="monitor">
        <TabsList className="bg-card rounded-sm flex-wrap h-auto">
          <TabsTrigger value="monitor" data-testid="admin-tab-monitor" className="rounded-sm gap-1"><Activity className="w-3.5 h-3.5" /> Monitor</TabsTrigger>
          <TabsTrigger value="activity" data-testid="admin-tab-activity" className="rounded-sm gap-1"><ScrollText className="w-3.5 h-3.5" /> Activity log</TabsTrigger>
          <TabsTrigger value="logins" data-testid="admin-tab-logins" className="rounded-sm gap-1"><LogIn className="w-3.5 h-3.5" /> Logins</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="admin-tab-compliance" className="rounded-sm">Compliance</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="admin-tab-pricing" className="rounded-sm">Pricing</TabsTrigger>
          <TabsTrigger value="customers" data-testid="admin-tab-customers" className="rounded-sm">Customers</TabsTrigger>
          <TabsTrigger value="subs" data-testid="admin-tab-subs" className="rounded-sm">Subscriptions</TabsTrigger>
          <TabsTrigger value="runs" data-testid="admin-tab-runs" className="rounded-sm">Agent runs</TabsTrigger>
          <TabsTrigger value="envs" data-testid="admin-tab-envs" className="rounded-sm">Environments</TabsTrigger>
          <TabsTrigger value="email" data-testid="admin-tab-email" className="rounded-sm">Email pipeline</TabsTrigger>
        </TabsList>

        {/* PRICING */}
        <TabsContent value="pricing" className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {packages.map((pkg) => (
            <div key={pkg.id} className="card-soft p-6" data-testid={`admin-pkg-${pkg.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading font-bold text-xl">{pkg.name}</h3>
                  <p className="font-mono text-xs text-secondary">{pkg.code}</p>
                </div>
                <Badge className={`rounded-sm ${pkg.active ? "bg-[#0F9D77]/15 text-[#0F9D77]" : "bg-muted text-secondary"}`}>{pkg.active ? "Active" : "Hidden"}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <Label className="text-xs text-secondary">Monthly price ($)</Label>
                  <Input type="number" value={pkg.monthly_price} onChange={(e) => editPkg(pkg.id, { monthly_price: e.target.value })}
                    data-testid={`admin-monthly-${pkg.id}`} className="rounded-sm bg-background border-border mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs text-secondary">Onboarding ($)</Label>
                  <Input type="number" value={pkg.onboarding_price} onChange={(e) => editPkg(pkg.id, { onboarding_price: e.target.value })}
                    data-testid={`admin-onboarding-${pkg.id}`} className="rounded-sm bg-background border-border mt-1.5" />
                </div>
              </div>
              <div className="mb-4">
                <Label className="text-xs text-secondary">Features (comma separated)</Label>
                <Input value={Array.isArray(pkg.features) ? pkg.features.join(", ") : pkg.features}
                  onChange={(e) => editPkg(pkg.id, { features: e.target.value.split(",").map((s) => s.trim()) })}
                  data-testid={`admin-features-${pkg.id}`} className="rounded-sm bg-background border-border mt-1.5 text-xs" />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-secondary">
                  <input type="checkbox" checked={pkg.active} onChange={(e) => editPkg(pkg.id, { active: e.target.checked })} data-testid={`admin-active-${pkg.id}`} /> Visible on site
                </label>
                <Button onClick={() => savePkg(pkg)} disabled={saving === pkg.id} data-testid={`admin-save-${pkg.id}`}
                  className="rounded-sm bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold gap-2">
                  {saving === pkg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* MONITOR */}
        <TabsContent value="monitor" className="mt-6"><AdminMonitor /></TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="activity" className="mt-6"><AdminActivityLog /></TabsContent>

        {/* COMPLIANCE */}
        <TabsContent value="compliance" className="mt-6"><AdminCompliance /></TabsContent>

        {/* LOGINS */}
        <TabsContent value="logins" className="mt-6 card-soft" data-testid="admin-logins">
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent">
              <TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Last login (UTC)</TableHead><TableHead>Logins</TableHead><TableHead>Failed</TableHead><TableHead>Last IP</TableHead><TableHead>Envs</TableHead><TableHead>Runs</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {logins.map((u) => (
                <TableRow key={u.id} className="border-border" data-testid={`login-row-${u.id}`}>
                  <TableCell><div className="font-medium">{u.name}</div><div className="text-xs text-secondary">{u.email}{u.company ? ` · ${u.company}` : ""}</div></TableCell>
                  <TableCell><Badge className={`rounded-full text-[10px] ${u.role === "admin" ? "bg-[#7C3AED]/15 text-[#7C3AED]" : "bg-muted text-secondary"}`}>{u.role}</Badge></TableCell>
                  <TableCell className="font-mono text-xs text-secondary">{u.last_login_at ? u.last_login_at.slice(0, 16).replace("T", " ") : "never"}</TableCell>
                  <TableCell className="font-mono text-sm">{u.login_count}</TableCell>
                  <TableCell className={`font-mono text-sm ${u.failed_login_count ? "text-[#E5325F]" : ""}`}>{u.failed_login_count}</TableCell>
                  <TableCell className="font-mono text-xs text-secondary">{u.last_ip || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{u.environment_count}</TableCell>
                  <TableCell className="font-mono text-sm">{u.run_count}</TableCell>
                  <TableCell>{u.role !== "admin" && (
                    <Button size="sm" variant="ghost" onClick={() => openAs(u)} data-testid={`login-open-as-${u.id}`} className="rounded-full h-8 gap-1 text-[#3A2DB8] hover:bg-[#3A2DB8]/10"><UserCog className="w-3.5 h-3.5" /> Open as</Button>
                  )}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        {/* CUSTOMERS */}
        <TabsContent value="customers" className="mt-6 card-soft">
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent">
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Company</TableHead><TableHead>Active subs</TableHead><TableHead>Joined</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {customers.map((c) => (
                <TableRow key={c.id} className="border-border" data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-secondary">{c.email}</TableCell>
                  <TableCell className="text-secondary">{c.company || "—"}</TableCell>
                  <TableCell>{c.subscription_count}</TableCell>
                  <TableCell className="text-secondary font-mono text-xs">{c.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => openAs(c)} data-testid={`admin-open-as-${c.id}`} className="rounded-full h-8 gap-1 text-[#3A2DB8] hover:bg-[#3A2DB8]/10">
                      <UserCog className="w-3.5 h-3.5" /> Open as customer
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {customers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-secondary py-8">No customers yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        {/* SUBSCRIPTIONS */}
        <TabsContent value="subs" className="mt-6 card-soft">
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent">
              <TableHead>Package</TableHead><TableHead>Status</TableHead><TableHead>Monthly</TableHead><TableHead>Activated</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {subs.map((s, i) => (
                <TableRow key={s.id || `${s.user_email}-${s.package_id}-${i}`} className="border-border" data-testid={`sub-row-${i}`}>
                  <TableCell className="font-medium">{s.package_name}</TableCell>
                  <TableCell><Badge className="rounded-sm bg-[#0F9D77]/15 text-[#0F9D77]">{s.status}</Badge></TableCell>
                  <TableCell className="font-mono">${(s.monthly_price || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-secondary font-mono text-xs">{s.activated_at?.slice(0, 10)}</TableCell>
                </TableRow>
              ))}
              {subs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-secondary py-8">No subscriptions yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        {/* RUNS */}
        <TabsContent value="runs" className="mt-6 card-soft">
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent">
              <TableHead>Environment</TableHead><TableHead>Customer</TableHead><TableHead>Platform</TableHead><TableHead>Status</TableHead><TableHead>Pass rate</TableHead><TableHead>Passed/Failed</TableHead><TableHead>When</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} className="border-border" data-testid={`admin-run-${r.id}`}>
                  <TableCell className="font-medium">{r.environment_name}{r.trigger === "scheduled" && <Badge className="ml-2 rounded-full bg-[#7C3AED]/10 text-[#7C3AED] text-[10px]">scheduled</Badge>}</TableCell>
                  <TableCell className="text-secondary text-xs">{r.customer_email || "—"}</TableCell>
                  <TableCell className="text-secondary">{r.product === "sap_successfactors" ? "SF" : "PC"}</TableCell>
                  <TableCell><Badge className={`rounded-full ${r.status === "completed" ? "bg-[#0F9D77]/15 text-[#0F9D77]" : r.status === "running" ? "bg-[#7C3AED]/15 text-[#7C3AED]" : "bg-[#E5325F]/15 text-[#E5325F]"}`}>{r.status || "completed"}</Badge></TableCell>
                  <TableCell><Badge className={`rounded-sm ${r.pass_rate >= 85 ? "bg-[#0F9D77]/15 text-[#0F9D77]" : "bg-[#FF8A3D]/15 text-[#FF8A3D]"}`}>{r.pass_rate}%</Badge></TableCell>
                  <TableCell className="font-mono text-sm"><span className="text-[#0F9D77]">{r.passed}</span> / <span className="text-[#E5325F]">{r.failed}</span></TableCell>
                  <TableCell className="text-secondary font-mono text-xs">{r.started_at?.slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/runs/${r.id}`)} data-testid={`admin-run-view-${r.id}`} className="rounded-full h-8 gap-1 text-[#3A2DB8] hover:bg-[#3A2DB8]/10">
                      <Eye className="w-3.5 h-3.5" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {runs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-secondary py-8">No runs yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        {/* ENVIRONMENTS */}
        <TabsContent value="envs" className="mt-6 card-soft">
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent">
              <TableHead>Environment</TableHead><TableHead>Customer</TableHead><TableHead>URL</TableHead><TableHead>Scripts</TableHead><TableHead>Last pass</TableHead><TableHead>Schedule</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {envs.map((e) => (
                <TableRow key={e.id} className="border-border" data-testid={`admin-env-${e.id}`}>
                  <TableCell className="font-medium">{e.name}<div className="text-[10px] text-secondary">{e.product === "sap_successfactors" ? "SuccessFactors" : "Public Cloud"}</div></TableCell>
                  <TableCell className="text-secondary text-xs">{e.customer_email || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-secondary max-w-[260px] truncate" title={e.env_url}>{e.env_url}</TableCell>
                  <TableCell className="font-mono text-sm">{e.approved_count || 0}/{e.script_count || 0}</TableCell>
                  <TableCell>{e.last_pass_rate != null ? <Badge className={`rounded-sm ${e.last_pass_rate >= 85 ? "bg-[#0F9D77]/15 text-[#0F9D77]" : "bg-[#FF8A3D]/15 text-[#FF8A3D]"}`}>{e.last_pass_rate}%</Badge> : <span className="text-secondary text-xs">—</span>}</TableCell>
                  <TableCell className="text-xs text-secondary">{e.schedule?.frequency || "off"}</TableCell>
                  <TableCell>
                    {e.last_run_id && (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/runs/${e.last_run_id}`)} data-testid={`admin-env-lastrun-${e.id}`} className="rounded-full h-8 gap-1 text-[#3A2DB8] hover:bg-[#3A2DB8]/10">
                        <Eye className="w-3.5 h-3.5" /> Last run
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {envs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-secondary py-8">No environments yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        {/* EMAIL */}
        <TabsContent value="email" className="mt-6">
          {email && (
            <div className="card-soft p-6 max-w-2xl" data-testid="admin-email-card">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5 text-[#7C3AED]" />
                <h3 className="font-heading font-bold text-xl">Email report pipeline</h3>
                <Badge className={`ml-auto rounded-full ${email.configured ? "bg-[#0F9D77]/15 text-[#0F9D77]" : "bg-[#FF8A3D]/15 text-[#B45309]"}`} data-testid="admin-email-status">
                  {email.configured ? "Active" : "Not configured"}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                {[["Provider", email.provider], ["Sender", email.sender || "—"], ["API key", email.key_hint || "—"],
                  ["Customers opted in", email.subscribed_customers], ["Reports emailed", email.emails_sent]].map(([l, v]) => (
                  <div key={l} className="border border-border rounded-lg p-3"><div className="text-[11px] text-secondary">{l}</div><div className="font-mono text-sm mt-1">{v}</div></div>
                ))}
              </div>
              {!email.configured && (
                <p className="text-xs text-secondary rounded-lg bg-muted p-3" data-testid="admin-email-setup">{email.setup} Until then customers see reports in-app only; the toggle remains available so they can opt in ahead of time.</p>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
